/**
 * =============================================================================
 * ListPageFiltersBar — Barra de filtros rápidos padronizada.
 *
 * Replica EXATAMENTE a barra de filtros da página Cobranças (Financeiro):
 *   - Container: `flex flex-wrap items-center gap-1 p-2 sm:gap-2 sm:p-3 border-b bg-muted/30`
 *   - Inputs/selects responsivos (`h-6 text-[11px] sm:h-8 sm:text-sm`)
 *   - Botão "Mais" pill (`rounded-full h-6 px-2 text-[10px] sm:h-8 sm:px-2.5 sm:text-xs`)
 *   - Indicador "Atualizando..." à direita
 *
 * Os controles (input de busca, selects) são passados como children — assim
 * cada página decide quais controles exibir, mas todos herdam o mesmo wrapper.
 *
 * Para garantir o tamanho responsivo padronizado nos campos, a página deve
 * aplicar a classe utilitária `LIST_FILTERS_INPUT_CLASS` exportada abaixo.
 *
 * @version 1.0.0
 * RELEASE: 2026-04-25 v1.0.0 - Criação do helper de filtros padronizado.
 * =============================================================================
 */
import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Classe utilitária para inputs/selects da barra de filtros.
 * Aplique em `<Input className={LIST_FILTERS_INPUT_CLASS} />` etc.
 */
export const LIST_FILTERS_INPUT_CLASS =
  'h-6 text-[11px] sm:h-8 sm:text-sm';

/**
 * Classe utilitária para SelectTrigger da barra de filtros (inclui largura).
 * Use em `<SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[130px]')} />`.
 */
export const LIST_FILTERS_SELECT_TRIGGER_CLASS =
  'w-[85px] h-6 text-[11px] sm:h-8 sm:text-sm';

interface ListPageFiltersBarProps {
  /** Controles de filtro (busca, selects, etc.). */
  children: React.ReactNode;
  /** Callback do botão "Mais" (abre Sheet de filtros avançados). */
  onMoreClick?: () => void;
  /** Quantidade de filtros avançados ativos (mostra badge). */
  moreCount?: number;
  /** Indica que está carregando dados em background. */
  isFetching?: boolean;
  /** Slot adicional à direita (ex.: botão Limpar). */
  rightSlot?: React.ReactNode;
  /** Classes adicionais. */
  className?: string;
}

/** Barra horizontal de filtros rápidos com fundo cinza claro. */
export function ListPageFiltersBar({
  children,
  onMoreClick,
  moreCount = 0,
  isFetching,
  rightSlot,
  className,
}: ListPageFiltersBarProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1 p-2 sm:gap-2 sm:p-3 border-b bg-muted/30',
        className,
      )}
    >
      {/* Controles passados pela página */}
      {children}

      {/* Botão "Mais" — pill */}
      {onMoreClick && (
        <Button
          variant="outline"
          size="sm"
          onClick={onMoreClick}
          className="h-6 px-2 text-[10px] sm:h-8 sm:px-2.5 sm:text-xs rounded-full gap-1.5"
        >
          <SlidersHorizontal className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          <span>Mais</span>
          {moreCount > 0 && (
            <Badge
              variant="secondary"
              className="h-4 min-w-[16px] px-1 text-[10px] rounded-full bg-primary/15 text-primary border-0"
            >
              {moreCount}
            </Badge>
          )}
        </Button>
      )}

      {/* Slot adicional à direita */}
      {rightSlot}

      {/* Indicador de fetching em background */}
      {isFetching && (
        <span className="text-[10px] sm:text-xs text-muted-foreground ml-auto">
          Atualizando...
        </span>
      )}
    </div>
  );
}
